import { useCallback, useEffect, useState } from 'react';
import {
  LOCAL_FOLDER_ACCESS_EVENT, getLocalFolderAccess, requestLocalFolderAccess,
  type LocalFolderAccess, type LocalFolderRequestResult,
} from '../../../utils/localSave';

export interface LocalFolderAccessControl {
  /** Null until the first check has finished. */
  access: LocalFolderAccess | null;
  /** Ask the browser for access. Call it straight from a click. */
  request: () => Promise<LocalFolderRequestResult>;
}

const sameAccess = (a: LocalFolderAccess | null, b: LocalFolderAccess): boolean =>
  !!a && a.state === b.state && ('name' in a ? a.name : undefined) === ('name' in b ? b.name : undefined);

/**
 * Whether the user's local auto-save folder can be read right now, kept up to date when
 * access is granted or the folder changes anywhere in the app (the Edit page, the
 * reconnect prompt, Settings, another tab). The value only changes when the answer does,
 * so views that depend on it do not redo their work for repeated events.
 */
export const useLocalFolderAccess = (userId?: string): LocalFolderAccessControl => {
  const [access, setAccess] = useState<LocalFolderAccess | null>(null);
  const update = useCallback((next: LocalFolderAccess) => {
    setAccess(prev => (sameAccess(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    let alive = true;
    const check = () => {
      getLocalFolderAccess(userId).then(
        (next) => { if (alive) update(next); },
        () => { if (alive) update({ state: 'none' }); },
      );
    };
    check();
    window.addEventListener(LOCAL_FOLDER_ACCESS_EVENT, check);
    return () => {
      alive = false;
      window.removeEventListener(LOCAL_FOLDER_ACCESS_EVENT, check);
    };
  }, [userId, update]);

  const request = useCallback(async () => {
    const result = await requestLocalFolderAccess(userId);
    // A grant fires the folder event, which checks again; other answers can change the state too.
    if (result !== 'granted') update(await getLocalFolderAccess(userId).catch((): LocalFolderAccess => ({ state: 'none' })));
    return result;
  }, [userId, update]);

  return { access, request };
};
